import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@mlm/ui';
import {
  COACHING_PROGRAM_LIMITS,
  COACHING_PROGRAM_MEDIA_KINDS,
  type CoachingProgramMediaKind,
} from '@mlm/shared';
import { hasRole, useHasPerm } from '../lib/auth-store';
import { Audience } from '../components/tier-labels';
import {
  createCoachingProgram,
  deleteCoachingProgram,
  getCoachingProgram,
  listCoachingContent,
  listCoachingPrograms,
  patchCoachingProgram,
  putCoachingProgramDays,
  type CoachingContentRow,
  type CoachingTier,
  type ProgramAdminDay,
  type ProgramAdminRow,
} from '../lib/api';
import { AudienceSelect } from './coaching-tool-editors';

/**
 * Admin → Coaching → Programs: the guided journeys members join from the
 * Achieve tab. A program is a title and a list of days; each day is a short
 * read, one prompt for Master Kala, and one micro-action that becomes that
 * day's #1 action. The built-in seven-day start is seeded from code and is
 * then an ordinary row here: edit it, hide it, or add programs of your own.
 */

const MEDIA_LABELS: Record<CoachingProgramMediaKind, string> = {
  none: 'No media',
  audio: 'An uploaded track',
  video: 'A video link',
};

type ProgramForm = {
  title: string;
  tagline: string;
  description: string;
  premiumLevel: CoachingTier;
  sortOrder: string;
};

const emptyDay = (dayNo: number): ProgramAdminDay => ({
  dayNo,
  title: '',
  lesson: '',
  mediaKind: 'none',
  mediaRef: null,
  kalaPrompt: '',
  actionTitle: '',
  actionMinutes: 10,
});

export function ProgramsTab() {
  const qc = useQueryClient();
  const canWrite = useHasPerm('coaching.content.write') || hasRole('super_admin');
  const q = useQuery({ queryKey: ['coaching-programs'], queryFn: listCoachingPrograms });
  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState<ProgramForm | null>(null);
  const [removing, setRemoving] = useState<ProgramAdminRow | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['coaching-programs'] });

  const create = useMutation({
    meta: { successMessage: 'Program created. Now write its days.' },
    mutationFn: (form: ProgramForm) =>
      createCoachingProgram({
        title: form.title.trim(),
        tagline: form.tagline.trim(),
        description: form.description.trim(),
        premiumLevel: form.premiumLevel,
        sortOrder: Number(form.sortOrder) || 0,
      }),
    onSuccess: (row) => {
      setCreating(null);
      setEditing(row.id);
      invalidate();
    },
  });
  const setActive = useMutation({
    mutationFn: (args: { id: string; active: boolean }) =>
      patchCoachingProgram(args.id, { active: args.active }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    meta: { successMessage: 'Program removed.' },
    mutationFn: (id: string) => deleteCoachingProgram(id),
    onSuccess: () => {
      setRemoving(null);
      if (editing === removing?.id) setEditing(null);
      invalidate();
    },
  });

  if (editing) {
    return (
      <ProgramEditor
        id={editing}
        canWrite={canWrite}
        onBack={() => {
          setEditing(null);
          invalidate();
        }}
      />
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Programs</CardTitle>
        <p className="text-sm text-muted-foreground">
          Guided journeys members join from Achieve, one at a time. Each day is a short read, a
          prompt for Master Kala, and one micro-action that becomes that day&rsquo;s #1 action.
          Nothing here may promise money or a health result.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {canWrite && !creating && (
          <Button
            size="sm"
            onClick={() =>
              setCreating({
                title: '',
                tagline: '',
                description: '',
                premiumLevel: 'free',
                sortOrder: '0',
              })
            }
          >
            Add a program
          </Button>
        )}
        {creating && (
          <div className="space-y-3 rounded-md border bg-muted/20 p-3">
            <p className="text-sm font-medium">New program</p>
            <ProgramFields form={creating} onChange={setCreating} />
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={creating.title.trim().length < 3 || create.isPending}
                onClick={() => create.mutate(creating)}
              >
                Create
              </Button>
              <Button size="sm" variant="outline" onClick={() => setCreating(null)}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {q.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {q.data && (
          <div className="divide-y rounded-md border">
            {q.data.map((p) => (
              <div
                key={p.id}
                className="flex flex-wrap items-start justify-between gap-3 p-3 text-sm"
              >
                <div className="min-w-0 space-y-1">
                  <p className={p.active ? '' : 'text-muted-foreground line-through'}>{p.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {p.daysCount} {p.daysCount === 1 ? 'day' : 'days'} ·{' '}
                    <Audience level={p.premiumLevel} /> · {p.activeEnrollments} in it now ·{' '}
                    {p.completedEnrollments} completed
                  </p>
                  <div className="flex gap-1">
                    {p.key && <Badge variant="secondary">Built-in</Badge>}
                    {!p.active && <Badge variant="secondary">Hidden</Badge>}
                    {p.daysCount === 0 && <Badge variant="secondary">No days yet</Badge>}
                  </div>
                </div>
                {canWrite && (
                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditing(p.id)}>
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={setActive.isPending}
                      onClick={() => setActive.mutate({ id: p.id, active: !p.active })}
                    >
                      {p.active ? 'Hide' : 'Show'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={p.activeEnrollments + p.completedEnrollments > 0}
                      title={
                        p.activeEnrollments + p.completedEnrollments > 0
                          ? 'Members have joined this program: hide it instead.'
                          : undefined
                      }
                      onClick={() => setRemoving(p)}
                    >
                      Remove
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
      <ConfirmDialog
        open={!!removing}
        onOpenChange={(open) => {
          if (!open) setRemoving(null);
        }}
        title="Remove this program?"
        description="Its days go with it. Nobody has joined it, so no member loses anything."
        confirmLabel="Remove"
        confirmVariant="destructive"
        loading={remove.isPending}
        onConfirm={async () => {
          await remove.mutateAsync(removing!.id);
        }}
      />
    </Card>
  );
}

function ProgramFields({
  form,
  onChange,
}: {
  form: ProgramForm;
  onChange: (f: ProgramForm) => void;
}) {
  return (
    <>
      <Input
        className="h-8"
        placeholder="Title, e.g. Your first seven days"
        maxLength={COACHING_PROGRAM_LIMITS.titleMax}
        value={form.title}
        onChange={(e) => onChange({ ...form, title: e.target.value })}
      />
      <Input
        className="h-8"
        placeholder="One line under the title"
        maxLength={COACHING_PROGRAM_LIMITS.taglineMax}
        value={form.tagline}
        onChange={(e) => onChange({ ...form, tagline: e.target.value })}
      />
      <Textarea
        rows={3}
        placeholder="What this program is and who it is for"
        maxLength={COACHING_PROGRAM_LIMITS.descriptionMax}
        value={form.description}
        onChange={(e) => onChange({ ...form, description: e.target.value })}
      />
      <div className="flex flex-wrap items-end gap-3">
        <AudienceSelect
          value={form.premiumLevel}
          onChange={(premiumLevel) => onChange({ ...form, premiumLevel })}
        />
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Order</label>
          <Input
            type="number"
            min={0}
            max={10000}
            className="h-8 w-24"
            value={form.sortOrder}
            onChange={(e) => onChange({ ...form, sortOrder: e.target.value })}
          />
        </div>
      </div>
    </>
  );
}

function ProgramEditor({
  id,
  canWrite,
  onBack,
}: {
  id: string;
  canWrite: boolean;
  onBack: () => void;
}) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['coaching-program', id], queryFn: () => getCoachingProgram(id) });
  const tracks = useQuery({
    queryKey: ['coaching-content', 'audio'],
    queryFn: () => listCoachingContent('audio'),
  });
  const [form, setForm] = useState<ProgramForm | null>(null);
  const [days, setDays] = useState<ProgramAdminDay[] | null>(null);
  useEffect(() => {
    if (!q.data) return;
    const p = q.data.program;
    setForm({
      title: p.title,
      tagline: p.tagline,
      description: p.description,
      premiumLevel: p.premiumLevel,
      sortOrder: String(p.sortOrder),
    });
    setDays(q.data.days);
  }, [q.data]);
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['coaching-program', id] });
    qc.invalidateQueries({ queryKey: ['coaching-programs'] });
  };

  const saveProgram = useMutation({
    meta: { successMessage: 'Program saved.' },
    mutationFn: (f: ProgramForm) =>
      patchCoachingProgram(id, {
        title: f.title.trim(),
        tagline: f.tagline.trim(),
        description: f.description.trim(),
        premiumLevel: f.premiumLevel,
        sortOrder: Number(f.sortOrder) || 0,
      }),
    onSuccess: invalidate,
  });
  const saveDays = useMutation({
    meta: { successMessage: 'Days saved.' },
    mutationFn: (list: ProgramAdminDay[]) =>
      putCoachingProgramDays(
        id,
        list.map((d, i) => ({
          dayNo: i + 1,
          title: d.title.trim(),
          lesson: d.lesson.trim(),
          mediaKind: d.mediaKind,
          mediaRef: d.mediaKind === 'none' ? null : d.mediaRef?.trim() || null,
          kalaPrompt: d.kalaPrompt.trim(),
          actionTitle: d.actionTitle.trim(),
          actionMinutes: d.actionMinutes,
        })),
      ),
    onSuccess: invalidate,
  });

  const dayValid = (d: ProgramAdminDay) =>
    d.title.trim().length >= 3 &&
    d.lesson.trim().length >= 20 &&
    d.kalaPrompt.trim().length >= 10 &&
    d.actionTitle.trim().length >= 3 &&
    (d.mediaKind === 'none' || Boolean(d.mediaRef?.trim()));
  const daysValid = !!days && days.length > 0 && days.every(dayValid);

  if (q.isLoading || !form || !days)
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!q.data)
    return <p className="text-sm text-muted-foreground">This program no longer exists.</p>;
  const p = q.data.program;

  return (
    <div className="space-y-4">
      <Button size="sm" variant="outline" onClick={onBack}>
        Back to programs
      </Button>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {p.title} {p.key && <Badge variant="secondary">Built-in</Badge>}
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            {p.activeEnrollments} in it now · {p.completedEnrollments} completed. Edits reach
            members on their next open; a member mid-way keeps their day count.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <ProgramFields form={form} onChange={setForm} />
          {canWrite && (
            <Button
              size="sm"
              disabled={form.title.trim().length < 3 || saveProgram.isPending}
              onClick={() => saveProgram.mutate(form)}
            >
              Save program
            </Button>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Days</CardTitle>
          <p className="text-sm text-muted-foreground">
            Day N opens N - 1 days after a member starts. Each read is plain text with blank lines
            between paragraphs; a line starting with “- ” is a bullet and **bold** is bold. The
            micro-action becomes that day&rsquo;s #1 action in the member&rsquo;s plan.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {days.map((d, i) => (
            <div key={i} className="space-y-3 rounded-md border bg-muted/20 p-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Day {i + 1}</p>
                {canWrite && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setDays(days.filter((_, j) => j !== i))}
                  >
                    Remove day
                  </Button>
                )}
              </div>
              <Input
                className="h-8"
                placeholder="Day title, e.g. Your starting point"
                maxLength={COACHING_PROGRAM_LIMITS.dayTitleMax}
                value={d.title}
                onChange={(e) =>
                  setDays(days.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))
                }
              />
              <Textarea
                rows={6}
                placeholder="The read for this day"
                maxLength={COACHING_PROGRAM_LIMITS.lessonMax}
                value={d.lesson}
                onChange={(e) =>
                  setDays(days.map((x, j) => (j === i ? { ...x, lesson: e.target.value } : x)))
                }
              />
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Media</label>
                  <Select
                    value={d.mediaKind}
                    onValueChange={(v) =>
                      setDays(
                        days.map((x, j) =>
                          j === i
                            ? { ...x, mediaKind: v as CoachingProgramMediaKind, mediaRef: null }
                            : x,
                        ),
                      )
                    }
                  >
                    <SelectTrigger className="h-8 w-44">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COACHING_PROGRAM_MEDIA_KINDS.map((k) => (
                        <SelectItem key={k} value={k}>
                          {MEDIA_LABELS[k]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {d.mediaKind === 'audio' && (
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Track</label>
                    <Select
                      value={d.mediaRef ?? ''}
                      onValueChange={(v) =>
                        setDays(days.map((x, j) => (j === i ? { ...x, mediaRef: v } : x)))
                      }
                    >
                      <SelectTrigger className="h-8 w-64">
                        <SelectValue placeholder="Choose an uploaded track" />
                      </SelectTrigger>
                      <SelectContent>
                        {(tracks.data ?? [])
                          .filter((t: CoachingContentRow) => t.active)
                          .map((t: CoachingContentRow) => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.title}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {d.mediaKind === 'video' && (
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Video url (https)</label>
                    <Input
                      className="h-8 w-80"
                      placeholder="https://www.youtube.com/watch?v=…"
                      value={d.mediaRef ?? ''}
                      onChange={(e) =>
                        setDays(
                          days.map((x, j) => (j === i ? { ...x, mediaRef: e.target.value } : x)),
                        )
                      }
                    />
                  </div>
                )}
              </div>
              <Textarea
                rows={2}
                placeholder="What lands in Master Kala’s message box when the member taps Ask, in the member’s voice"
                maxLength={COACHING_PROGRAM_LIMITS.promptMax}
                value={d.kalaPrompt}
                onChange={(e) =>
                  setDays(days.map((x, j) => (j === i ? { ...x, kalaPrompt: e.target.value } : x)))
                }
              />
              <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[16rem] flex-1 space-y-1">
                  <label className="text-xs text-muted-foreground">Micro-action</label>
                  <Input
                    className="h-8"
                    placeholder="e.g. Complete the morning ritual once"
                    maxLength={COACHING_PROGRAM_LIMITS.actionTitleMax}
                    value={d.actionTitle}
                    onChange={(e) =>
                      setDays(
                        days.map((x, j) => (j === i ? { ...x, actionTitle: e.target.value } : x)),
                      )
                    }
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Minutes</label>
                  <Input
                    type="number"
                    min={1}
                    max={COACHING_PROGRAM_LIMITS.actionMinutesMax}
                    className="h-8 w-24"
                    value={d.actionMinutes ?? ''}
                    onChange={(e) =>
                      setDays(
                        days.map((x, j) =>
                          j === i
                            ? {
                                ...x,
                                actionMinutes: e.target.value ? Number(e.target.value) : null,
                              }
                            : x,
                        ),
                      )
                    }
                  />
                </div>
              </div>
            </div>
          ))}
          {canWrite && (
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={days.length >= COACHING_PROGRAM_LIMITS.daysMax}
                onClick={() => setDays([...days, emptyDay(days.length + 1)])}
              >
                Add a day
              </Button>
              <Button
                size="sm"
                disabled={!daysValid || saveDays.isPending}
                onClick={() => saveDays.mutate(days)}
              >
                Save days
              </Button>
            </div>
          )}
          {!daysValid && days.length > 0 && (
            <p className="text-xs text-muted-foreground">
              Every day needs a title, a read of at least 20 characters, a prompt of at least 10, a
              micro-action, and its media when one is chosen.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
